import { h, type VNode } from 'vue'
import { createGlobalState } from '@vueuse/core'
import { type ExternalToast, toast as sonnerToast } from 'vue-sonner'
import Icon from '../icon/Icon.vue'

export type ToastOptions = ExternalToast & {
    title?: string | VNode
    variant?: 'success' | 'missing' | 'error' | 'warning' | 'info'
}

export type ToastOptionsWithoutVariant = Omit<ToastOptions, 'variant'>

const iconMap = {
    success: h(Icon, { name: 'check-circle', size: 16, class: 'text-success-core' }),
    missing: h(Icon, { name: 'exclamation-circle', size: 16, class: 'text-missing-core' }),
    error: h(Icon, { name: 'exclamation-circle', size: 16, class: 'text-error-core' }),
    warning: h(Icon, { name: 'minus-circle', size: 16, class: 'text-warning-core' }),
    info: h(Icon, { name: 'information-circle', size: 16, class: 'text-cta' }),
}
export const useToast = createGlobalState(() => {
    // Track the last time a toast was triggered and the queue size
    let lastToastTime = 0
    let toastQueue = 0
    const QUEUE_RESET_THRESHOLD = 300 // ms - threshold to reset queue counter
    const TOAST_DELAY = 50 // ms - delay between consecutive toasts

    function toast(props: ToastOptions) {
        const { variant = 'info', duration, ...rest } = props

        const triggerToast = () => {
            const now = Date.now()
            const timeSinceLastToast = now - lastToastTime

            // Reset queue if enough time has passed
            if (timeSinceLastToast > QUEUE_RESET_THRESHOLD) {
                toastQueue = 0
            }

            const currentDelay = toastQueue * TOAST_DELAY

            // Update timestamp and increment queue
            lastToastTime = now
            toastQueue++

            setTimeout(() => {
                sonnerToast(props.title ?? '', {
                    // defaults
                    icon: iconMap[variant as keyof typeof iconMap ?? 'info'],
                    richColors: true,
                    duration: duration === 0 ? Infinity : (duration ?? 3000),
                    ...{ type: variant },
                    ...rest,
                })
            }, currentDelay)
        }

        // if toast with the same id exists, dismiss it first, wait for animation to finish and then trigger the new toast
        if (props.id) {
            const toasts = sonnerToast.getToasts()
            const existingToast = toasts.find(toast => toast.id === props.id)
            if (existingToast) {
                sonnerToast.dismiss(existingToast.id)
                setTimeout(triggerToast, 300)
                return
            }
        }

        triggerToast()
    }

    toast.info = toastFor('info')
    toast.success = toastFor('success')
    toast.missing = toastFor('missing')
    toast.warning = toastFor('warning')
    toast.error = toastFor('error')

    function toastFor(variant: ToastOptions['variant']) {
        function t(options: ToastOptionsWithoutVariant): void
        function t(message: string): void
        function t(arg: string | ToastOptionsWithoutVariant): void {
            return typeof arg === 'string'
                ? toast({ description: arg, variant })
                : toast({ ...arg, variant })
        }
        return t
    }

    return {
        toast,
    }
})
